import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  PhotoIcon,
  StarIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { productsAPI } from '../../utils/api';
import { Product } from '../../types';
import AddProductModal from '../../components/admin/AddProductModal';
import EditProductModal from '../../components/admin/EditProductModal';

const ProductsManagement: React.FC = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [deleteModal, setDeleteModal] = useState<{ show: boolean; product: Product | null }>({
    show: false,
    product: null
  });
  const [addProductModal, setAddProductModal] = useState(false);
  const [editProductModal, setEditProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  // Amrod-style categories
  const categories = [
    { id: 'gifts', name: 'Corporate Gifts', subcategories: [
      'Executive Gifts', 'Promotional Items', 'Tech Accessories', 'Stationery',
      'Drinkware', 'Bags & Briefcases', 'Home & Living', 'Sports & Wellness',
      'Writing Instruments', 'Personal Care'
    ]},
        { id: 'wear', name: 'Corporate Wear & Uniforms', subcategories: [
      'T-Shirts & Polos', 'Jackets & Hoodies', 'Workwear', 'Formal Wear',
      'Outdoor Apparel', 'Safety Wear', 'Footwear', 'Accessories'
    ]},
    { id: 'branding', name: 'Branding & Signage', subcategories: [
      'Logo Design', 'Business Cards', 'Stationery Sets', 'Office Signage',
      'Vehicle Branding', 'Window Graphics', 'Interior Signage', 'Brand Guidelines'
    ]},
    { id: 'printing', name: 'Printing & Display', subcategories: [
      'Banners & Flags', 'Brochures & Flyers', 'Large Format Printing', 'Display Stands',
      'Trade Show Materials', 'Posters & Posters', 'Business Cards', 'Stickers & Labels'
    ]},
    { id: 'collections', name: 'Collections & Themes', subcategories: [
      'Executive Collection', 'Tech Collection', 'Outdoor Collection', 'Wellness Collection',
      'Eco-Friendly', 'Premium Gifts', 'Budget Options', 'Seasonal Items'
    ]}
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const response = await productsAPI.getProducts({ 
        limit: 100,
        ...(searchTerm && { search: searchTerm }),
        ...(selectedCategory && { category: selectedCategory })
      });
      
      if (response.data.success && response.data.data) {
        setProducts(response.data.data.products);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (product: Product) => {
    try {
      await productsAPI.deleteProduct(product._id);
      setProducts(products.filter(p => p._id !== product._id));
      setDeleteModal({ show: false, product: null });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete product');
    }
  };

  const handleEditProduct = (product: Product) => {
    setSelectedProduct(product);
    setEditProductModal(true);
  };

  if (loading) {
    return (
      <div className="space-y-8">
        {/* Header skeleton */}
        <div className="border-b border-gray-200 pb-6">
          <div className="flex justify-between items-center">
            <div>
              <div className="h-10 bg-gray-200 rounded-lg w-48 animate-pulse mb-3"></div>
              <div className="h-5 bg-gray-200 rounded w-64 animate-pulse"></div>
            </div>
            <div className="h-11 bg-gray-200 rounded-lg w-36 animate-pulse"></div>
          </div>
        </div>
        
        {/* Filters skeleton */}
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 animate-pulse">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="h-20 bg-gray-200 rounded-lg"></div>
            <div className="h-20 bg-gray-200 rounded-lg"></div>
            <div className="h-20 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
        
        {/* Products grid skeleton */}
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-pulse">
              <div className="h-56 bg-gray-200"></div>
              <div className="p-6">
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                <div className="h-3 bg-gray-200 rounded w-full mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3 mb-6"></div>
                <div className="flex space-x-2">
                  <div className="h-8 bg-gray-200 rounded flex-1"></div>
                  <div className="h-8 bg-gray-200 rounded flex-1"></div>
                  <div className="h-8 bg-gray-200 rounded flex-1"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="border-b border-gray-200 pb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 tracking-tight">Products</h1>
            <p className="text-lg text-gray-600 mt-3">Manage your product catalog and inventory</p>
          </div>
          <button
            onClick={() => setAddProductModal(true)}
            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary to-primary-dark text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-primary/25 transition-all duration-200 transform hover:-translate-y-0.5"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Add Product
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div className="flex flex-col lg:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Search Products
            </label>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 bg-gray-50/50"
              />
            </div>
          </div>

          <div className="flex-1">
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Category
            </label>
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-colors duration-200 bg-gray-50/50"
            >
              <option value="">All Categories</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
          </div>

          <div className="w-full lg:w-auto">
            <button
              onClick={fetchProducts}
              className="w-full lg:w-auto inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-accent to-accent-dark text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-accent/25 transition-all duration-200 transform hover:-translate-y-0.5"
            >
              <FunnelIcon className="h-5 w-5 mr-2" />
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Products Grid */}
      {products.length === 0 ? (
        <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 py-20">
          <div className="text-center">
            <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-6">
              <PhotoIcon className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 mb-3">
              {searchTerm || selectedCategory ? 'No products found' : 'No products yet'}
            </h3>
            <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">
              {searchTerm || selectedCategory 
                ? 'Try adjusting your search criteria or browse all products'
                : 'Start building your product catalog by adding your first product!'
              }
            </p>
            <Link 
              to="/admin/products/new" 
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-primary to-primary-dark text-white font-semibold rounded-xl hover:shadow-lg hover:shadow-primary/25 transition-all duration-200 transform hover:-translate-y-0.5"
            >
              <PlusIcon className="h-6 w-6 mr-3" />
              {searchTerm || selectedCategory ? 'Add New Product' : 'Add Your First Product'}
            </Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((product) => (
            <div key={product._id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md hover:shadow-gray-200/50 transition-all duration-200 group">
              {/* Product Image */}
              <div className="relative h-48 bg-gray-100 overflow-hidden">
                <img
                  src={product.images?.[0] || '/api/placeholder/400/300'}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                />
              </div>

              {/* Product Info */}
              <div className="p-6">
                {/* Product Name */}
                <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-primary transition-colors duration-200">
                  {product.name}
                </h3>

                {/* Category */}
                <div className="mb-3">
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                    {categories.find(cat => cat.id === product.category)?.name || product.category}
                  </span>
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm mb-6 line-clamp-3">
                  {product.description}
                </p>

                {/* Actions */}
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditProduct(product)}
                    className="flex-1 flex items-center justify-center px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                  >
                    <PencilIcon className="h-4 w-4 mr-1.5" />
                    Edit
                  </button>
                  <button
                    onClick={() => setDeleteModal({ show: true, product })}
                    className="flex-1 flex items-center justify-center px-3 py-2.5 border border-red-200 rounded-lg text-sm font-semibold text-red-700 bg-white hover:bg-red-50 hover:border-red-300 transition-all duration-200"
                  >
                    <TrashIcon className="h-4 w-4 mr-1.5" />
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && deleteModal.product && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <div className="fixed inset-0 bg-gray-900/50 backdrop-blur-sm" onClick={() => setDeleteModal({ show: false, product: null })} />
            <div className="relative bg-white rounded-xl shadow-2xl p-8 max-w-md w-full border border-gray-100">
              <div className="flex items-center mb-6">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-16 w-16 rounded-full bg-red-100">
                  <TrashIcon className="h-8 w-8 text-red-600" />
                </div>
              </div>
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Delete Product
                </h3>
                <p className="text-gray-600 mb-8 leading-relaxed">
                  Are you sure you want to delete <span className="font-semibold text-gray-900">"{deleteModal.product.name}"</span>? This action cannot be undone.
                </p>
                <div className="flex space-x-4">
                  <button
                    onClick={() => setDeleteModal({ show: false, product: null })}
                    className="flex-1 px-6 py-3 border border-gray-200 rounded-xl text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleDelete(deleteModal.product!)}
                    className="flex-1 px-6 py-3 bg-red-600 border border-transparent rounded-xl text-sm font-semibold text-white hover:bg-red-700 hover:shadow-lg hover:shadow-red-600/25 transition-all duration-200 transform hover:-translate-y-0.5"
                  >
                    Delete Product
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      <AddProductModal
        isOpen={addProductModal}
        onClose={() => setAddProductModal(false)}
        onSuccess={() => {
          fetchProducts();
        }}
      />

      {/* Edit Product Modal */}
      <EditProductModal
        isOpen={editProductModal}
        onClose={() => setEditProductModal(false)}
        onSuccess={() => {
          fetchProducts();
        }}
        product={selectedProduct}
      />
    </div>
  );
};

export default ProductsManagement;
